import React from "react"
import { User as UserIcon, Edit, Save, X, Eye, EyeOff, CheckCircle, XCircle, Loader2 } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Button } from "../ui/button"
import { usersApi } from "../../services/usersApi"

interface User {
  first_name: string
  last_name: string
  email: string
  password?: string
  confirm_password?: string
}

interface UserInformationCardProps {
  userData: User
  errors: { [key: string]: string[] }
  onDataChange: (field: keyof User, value: string) => void
  alwaysEditable?: boolean
  viewOnly?: boolean
  isEditing?: boolean
  onEdit?: () => void
  onSave?: (data: User) => void
  onCancel?: () => void
  isNewUser?: boolean
  userId?: string  // Add userId for email validation
  onEmailValidationChange?: (isValid: boolean, isAvailable: boolean | null) => void  // Add callback for email validation state
}

export default function UserInformationCard({
  userData,
  errors,
  onDataChange,
  alwaysEditable = false,
  viewOnly = false,
  isEditing = false,
  onEdit,
  onSave,
  onCancel,
  isNewUser = false,
  userId,
  onEmailValidationChange,
}: UserInformationCardProps) {
  const [showPassword, setShowPassword] = React.useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false)
  const [emailChecking, setEmailChecking] = React.useState(false)
  const [emailAvailable, setEmailAvailable] = React.useState<boolean | null>(null)
  const [emailMessage, setEmailMessage] = React.useState("")

  // const isViewMode = viewOnly && !alwaysEditable // Currently unused

  const handleInputChange = (field: keyof User, value: string) => {
    onDataChange(field, value)
    
    // Reset email validation when email changes
    if (field === 'email') {
      setEmailAvailable(null)
      setEmailMessage("")
    }
  }
  
  const handleSave = () => {
    if (onSave) {
      onSave(userData)
    }
  }

  // Debounced email availability check
  React.useEffect(() => {
    if ((!isEditing && !alwaysEditable) || !userData.email || userData.email.length < 3) {
      setEmailAvailable(null)
      setEmailMessage("")
      return
    }

    // First validate email format
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
    
    if (!emailRegex.test(userData.email)) {
      setEmailAvailable(null)
      setEmailMessage("Please enter a valid email address")
      return
    }

    // Additional email format checks
    const [localPart, domain] = userData.email.split('@')
    if (localPart.length > 64) {
      setEmailAvailable(null)
      setEmailMessage("Email local part cannot exceed 64 characters")
      return
    }
    if (domain.length > 253) {
      setEmailAvailable(null)
      setEmailMessage("Email domain cannot exceed 253 characters")
      return
    }
    if (localPart.length === 0) {
      setEmailAvailable(null)
      setEmailMessage("Email local part cannot be empty")
      return
    }
    if (domain.length === 0) {
      setEmailAvailable(null)
      setEmailMessage("Email domain cannot be empty")
      return
    }

    // Don't check if it's the same email as current user
    if (userId && emailMessage === "Email is available") {
      return
    }

    const timer = setTimeout(async () => {
      try {
        setEmailChecking(true)
        const response = await usersApi.checkEmailAvailability(userData.email, userId)
        setEmailAvailable(response.available)
        setEmailMessage(response.message)
      } catch (error) {
        console.error('Email availability check failed:', error)
        setEmailAvailable(null)
        setEmailMessage("Unable to verify email")
      } finally {
        setEmailChecking(false)
      }
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [userData.email, isEditing, alwaysEditable, userId, emailMessage])

  // Get email field styling based on validation state
  const getEmailFieldStyle = () => {
    if (emailChecking) return "border-slate-300"
    if (emailAvailable === true) return "border-green-500"
    if (emailAvailable === false) return "border-red-500"
    if (emailMessage && emailMessage !== "Email is available" && emailMessage !== "Email is already in use") {
      return "border-red-500" // Format validation error
    }
    return errors.email ? "border-red-500" : "border-slate-300"
  }

  // Get email validation icon
  const getEmailIcon = () => {
    if (emailChecking) {
      return <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />
    }
    if (emailAvailable === true) {
      return <CheckCircle className="h-4 w-4 text-green-500" />
    }
    if (emailAvailable === false) {
      return <XCircle className="h-4 w-4 text-red-500" />
    }
    // Show error icon for format validation errors
    if (emailMessage && emailMessage !== "Email is available" && emailMessage !== "Email is already in use") {
      return <XCircle className="h-4 w-4 text-red-500" />
    }
    return null
  }

  // Check if save should be disabled
  const isSaveDisabled = () => {
    return emailAvailable === false || 
           (emailMessage && emailMessage !== "Email is available" && emailMessage !== "Email is already in use")
  }

  // Check if email validation should be shown
  const shouldShowEmailValidation = () => {
    return isEditing || alwaysEditable
  }

  // Notify parent component about email validation changes
  React.useEffect(() => {
    if (onEmailValidationChange) {
      const isValid = !emailMessage || emailMessage === "Email is available" || emailMessage === "Email is already in use"
      onEmailValidationChange(isValid, emailAvailable)
    }
  }, [emailMessage, emailAvailable, onEmailValidationChange])

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="border-b border-slate-200">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <UserIcon className="h-5 w-5" />
            User Information
          </CardTitle>
          {!alwaysEditable && (
            <div className="flex items-center gap-2">
              {viewOnly && !isEditing && onEdit && (
                <Button size="sm" onClick={onEdit} className="bg-slate-900 hover:bg-slate-800 text-white">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
              {isEditing && (
                <>
                  <Button type="button" variant="outline" size="sm" onClick={onCancel}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button 
                    type="button" 
                    size="sm" 
                    onClick={handleSave} 
                    className="bg-green-600 hover:bg-green-700"
                    disabled={isSaveDisabled() || false} // Ensure boolean value
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* First Name */}
          <div className="space-y-2">
            <Label htmlFor="first_name" className="text-sm font-medium text-slate-700">
              First Name *
            </Label>
            {(isEditing || alwaysEditable) ? (
              <Input
                id="first_name"
                type="text"
                value={userData.first_name}
                onChange={(e) => handleInputChange("first_name", e.target.value)}
                placeholder="Enter first name"
                className={errors.first_name ? "border-red-500" : ""}
              />
            ) : (
              <div className="p-2 bg-slate-50 rounded-md border border-slate-200">
                <p className="text-sm text-slate-900">{userData.first_name}</p>
              </div>
            )}
            {errors.first_name && (
              <p className="text-sm text-red-500">{errors.first_name[0]}</p>
            )}
          </div>

          {/* Last Name */}
          <div className="space-y-2">
            <Label htmlFor="last_name" className="text-sm font-medium text-slate-700">
              Last Name *
            </Label>
            {(isEditing || alwaysEditable) ? (
              <Input
                id="last_name"
                type="text"
                value={userData.last_name}
                onChange={(e) => handleInputChange("last_name", e.target.value)}
                placeholder="Enter last name"
                className={errors.last_name ? "border-red-500" : ""}
              />
            ) : (
              <div className="p-2 bg-slate-50 rounded-md border border-slate-200">
                <p className="text-sm text-slate-900">{userData.last_name}</p>
              </div>
            )}
            {errors.last_name && (
              <p className="text-sm text-red-500">{errors.last_name[0]}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-slate-700">
              Email Address *
            </Label>
            {(isEditing || alwaysEditable) ? (
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  value={userData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="Enter email address"
                  className={`pr-10 ${getEmailFieldStyle()}`}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {shouldShowEmailValidation() && getEmailIcon()}
                </div>
              </div>
            ) : (
              <div className="p-2 bg-slate-50 rounded-md border border-slate-200">
                <p className="text-sm text-slate-900">{userData.email}</p>
              </div>
            )}
            {/* Email validation message */}
            {(isEditing || alwaysEditable) && emailMessage && (
              <p className={`text-sm ${
                emailAvailable === true ? 'text-green-600' : 
                emailAvailable === false ? 'text-red-600' : 
                emailMessage.includes('valid') || emailMessage.includes('cannot') ? 'text-red-600' : 
                'text-slate-600'
              }`}>
                {emailChecking ? "Checking email availability..." : emailMessage}
              </p>
            )}
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email[0]}</p>
            )}
          </div>

          {/* Password - Only show for new users or when editing */}
          {isNewUser && (
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                Password *
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={userData.password || ""}
                  onChange={(e) => handleInputChange("password", e.target.value)}
                  placeholder="Enter password"
                  className={errors.password ? "border-red-500 pr-10" : "pr-10"}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-slate-500" />
                  ) : (
                    <Eye className="h-4 w-4 text-slate-500" />
                  )}
                </Button>
              </div>
              {errors.password && (
                <p className="text-sm text-red-500">{errors.password[0]}</p>
              )}
            </div>
          )}

          {/* Confirm Password - Only show for new users */}
          {isNewUser && (
            <div className="space-y-2">
              <Label htmlFor="confirm_password" className="text-sm font-medium text-slate-700">
                Confirm Password *
              </Label>
              <div className="relative">
                <Input
                  id="confirm_password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={userData.confirm_password || ""}
                  onChange={(e) => handleInputChange("confirm_password", e.target.value)}
                  placeholder="Confirm password"
                  className={errors.confirm_password ? "border-red-500 pr-10" : "pr-10"}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-slate-500" />
                  ) : (
                    <Eye className="h-4 w-4 text-slate-500" />
                  )}
                </Button>
              </div>
              {errors.confirm_password && (
                <p className="text-sm text-red-500">{errors.confirm_password[0]}</p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
} 